<script setup lang="tsx">
import { computed, onMounted, ref, watch, type WatchStopHandle } from 'vue'
import type { CustomFieldDefinition, Response, SaveCustomFieldsRequest } from '@/models/api-generated'
import http from '@/services/http'
import { CF_Calendar, CF_Checkbox, CF_Select, CF_Text, CF_User, CF_URL } from '@/models/enums-generated'
import Multiselect from 'vue-multiselect'
import { setNavLock } from '@/services/navlock'
import Modal from '../shared/Modal.vue'
import IconButton from '../shared/IconButton.vue'
import CFToolBar from './CFToolBar.vue'

const fields = ref<{ [table: string]: CustomFieldDefinition[] }>({
	business_processes: [],
	assets: [],
	sub_assets: [],
	data_assets: [],
	standard_methods: [],
	standard_method_meta_data: [],
	users: [],
})

const emit = defineEmits<{
	(e: 'hasChanges', val: boolean): void
}>()

const multiselectRef = ref<any[]>()
const showConfirmDialog = ref(false)
const current = ref<CustomFieldDefinition>()
const changed = ref(new Set<number>())
const deleted = ref(new Set<number>())
const watches: WatchStopHandle[] = []
let tab = ''

onMounted(async () => await fetch())

async function fetch() {
	for (const t of Object.keys(fields.value)) {
		const { data } = await http.get<Response<CustomFieldDefinition[]>>(`api/customFields/${t}`)
		fields.value[t] = data.data ?? []

		for (let i = 0; i < fields.value[t].length; i++) {
			watches.push(
				watch(
					() => fields.value[t][i],
					(change) => {
						if (change) changed.value.add(change.id)
					},
					{ deep: true },
				),
			)
		}
	}
}

watch(
	multiselectRef,
	() => {
		if (!multiselectRef.value) return

		for (const multi of multiselectRef.value) {
			const input = multi.$el.querySelector('input.multiselect__input') as HTMLInputElement
			input.addEventListener('keyup', (e) => {
				if (e.key === 'Enter') {
					const currentDef = current.value
					if (!currentDef) return
					if (!currentDef.values) currentDef.values = []

					if (multi.search && !currentDef.values.find((x) => x === multi.search)) {
						current.value?.values.push(multi.search)
						multi.search = ''
					}
				}
			})
		}
	},
	{ deep: true },
)

function addField(table: string) {
	fields.value[table].push({
		id: 0,
		table,
		label: '',
		type: CF_Text,
		values: [],
	})
}

function deleteField(def: CustomFieldDefinition) {
	if (def.id === 0) {
		const idx = fields.value[def.table].indexOf(def)
		if (idx >= 0) fields.value[def.table].splice(idx, 1)
	} else {
		if (deleted.value.has(def.id)) deleted.value.delete(def.id)
		else deleted.value.add(def.id)
	}
}

const all = computed(() => Object.values(fields.value).flat())

const numChanges = computed(() => changed.value.size + deleted.value.size + all.value.filter((x) => x.id === 0).length)

const canSave = computed(() => {
	if (!numChanges.value) return false

	for (const def of all.value) {
		if (!def.label) return false
		if (def.type === CF_Select && !def.values?.length) return false
	}

	return true
})

watch(canSave, (val) => {
	emit('hasChanges', val)
})
watch(canSave, (can) => setNavLock('settings-custom-fields', can))

async function save() {
	const request: SaveCustomFieldsRequest = {
		added: all.value.filter((x) => x.id === 0),
		changed: all.value.filter((x) => changed.value.has(x.id) && !deleted.value.has(x.id)),
		deleted: all.value.map((x) => x.id).filter((x) => deleted.value.has(x)),
	}

	await http.post('api/customFields', request)
	await reset()
}

async function reset() {
	showConfirmDialog.value = false

	watches.forEach((x) => x())
	watches.splice(0, watches.length)
	for (const t of Object.keys(fields.value)) fields.value[t] = []
	changed.value.clear()
	deleted.value.clear()

	await fetch()
}

async function startReset() {
	if (canSave.value) showConfirmDialog.value = true
	else await reset()
}
</script>

<template>
	<div class="mt-4">
		<div class="rounded-container p-3">
			<h5 class="mb-3">{{ $t('customFields') }}</h5>
			<ul class="nav nav-tabs" role="tablist">
				<li class="nav-item" role="presentation" v-for="(table, i) in Object.keys(fields)" :key="table" :set="(tab = `cf-${table}`)">
					<button
						:id="`cf-tab-btn-${table}`"
						:data-bs-target="'#' + tab"
						:aria-controls="tab"
						:class="{ active: i === 0 }"
						:aria-selected="i === 0"
						class="nav-link"
						data-bs-toggle="tab"
						type="button"
						role="tab"
					>
						{{ $t('customField.tables.' + table) }}
					</button>
				</li>
			</ul>
			<div class="tab-content mt-4">
				<div
					v-for="(table, i) in Object.keys(fields)"
					:key="table"
					:set="(tab = `cf-${table}`)"
					class="tab-pane"
					:class="{ show: i === 0, active: i === 0 }"
					:id="tab"
					role="tabpanel"
				>
					<div
						class="d-flex flex-wrap align-items-start gap-2 mt-2 w-100"
						v-for="(def, i) in fields[table]"
						:key="def.id"
						:class="{ 'is-changed': def.id === 0 || (changed.has(def.id) && !deleted.has(def.id)) }"
					>
						<div class="col-3 flex-grow-1">
							<input
								:id="`cf-label-${table}-${i}`"
								type="text"
								class="form-control"
								:class="{ 'is-invalid': !fields[table][i].label }"
								:disabled="deleted.has(def.id)"
								v-model="fields[table][i].label"
								autocomplete="off"
							/>
						</div>
						<div class="col-3 flex-grow-1">
							<select
								:id="`cf-type-${table}-${i}`"
								class="form-select"
								:class="{ 'is-invalid': def.type === CF_Select && !def.values.length }"
								:disabled="deleted.has(def.id)"
								v-model="fields[table][i].type"
							>
								<option :value="CF_Text">{{ $t('customField.text') }}</option>
								<option :value="CF_Select">{{ $t('customField.select') }}</option>
								<option :value="CF_Checkbox">{{ $t('customField.checkbox') }}</option>
								<option :value="CF_Calendar">{{ $t('customField.calendar') }}</option>
								<option :value="CF_User">{{ $t('customField.user') }}</option>
								<option :value="CF_URL">URL</option>
							</select>
						</div>
						<div class="col-1 flex-grow-1">
							<Multiselect
								:id="`cf-values-${table}-${i}`"
								v-if="def.type === CF_Select"
								ref="multiselectRef"
								open-direction="above"
								:disabled="deleted.has(def.id)"
								:multiple="true"
								:searchable="true"
								:allow-empty="true"
								:show-labels="false"
								:use-teleport="true"
								:show-no-options="false"
								:show-no-results="false"
								:internal-search="false"
								v-model="fields[table][i].values"
								:options="[]"
								:placeholder="$t('customField.inputValue')"
								@search-change="current = fields[table][i]"
							/>
						</div>
						<div class="flex-shrink-0 d-flex">
							<IconButton
								:id="`cf-delete-${table}-${i}`"
								class="bi bi-trash"
								:class="deleted.has(def.id) ? 'active' : ''"
								:title="$t('misc.delete')"
								@click="deleteField(def)"
							/>
						</div>
					</div>

					<div class="row mt-2">
						<div class="col text-end">
							<IconButton :id="`cf-add-${table}`" class="bi bi-plus-square" :title="$t('misc.add')" @click="addField(table)" />
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
	<CFToolBar :numChanges="numChanges" :canSave="canSave" @save="save" @reset="startReset" />
	<Modal v-model="showConfirmDialog" @confirm="reset">
		<template #header>
			<h5>{{ $t('misc.confirm') }}</h5>
		</template>
		<template #body>
			<p>{{ $t('misc.loseUnsavedChanges') }}</p>
		</template>
	</Modal>
</template>

<style lang="sass" scoped>
.is-changed
  input:not(.is-invalid), select:not(.is-invalid)
    border: 1px solid blue

.bi-floppy:not(.btn-icon-disabled)
  color: var(--bs-primary)

.bi-trash.active,
.bi-trash:hover
  color: var(--bs-danger)
</style>

<style lang="sass">
.is-changed .multiselect:not(.multiselect--active) .multiselect__tags
  border: 1px solid blue
</style>
